import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Paper,
} from '@mui/material';
import dayjs from 'dayjs';
import { alpha, useTheme } from '@mui/material/styles';
import { getVesselScheduleHistory } from '../../../services/ScheduleRepository';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardDoubleArrowRightOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowRightOutlined';
import KeyboardDoubleArrowDownOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowDownOutlined';
import KeyboardDoubleArrowUpOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowUpOutlined';
import Grid from '@mui/material/Grid';

const DialogComponentHistory = ({ open, handleClose, row }) => {
  const [historyData, setHistoryData] = useState([]);
  const theme = useTheme();

  // Effect to fetch and set form data and history data when the dialog is opened or the row changes
  useEffect(() => {
    if (open && row) {
      // Fetch history data and include the current row data as the latest version
      const fetchHistory = async () => {
        try {
          const historyData = await getVesselScheduleHistory(row.id);
          setHistoryData([...historyData, row]);
        } catch (error) {
          console.error('Error fetching History:', error);
        }
      };
      fetchHistory();
    }
  }, [open, row]);

  // Function to format dates to German date format without time
  const formatDate = (dateString) => {
    return dayjs(dateString).format('DD.MM.YYYY');
  };

  // Function to get the fields that have changed between two versions
  const getChangedFields = (newer, older) => {
    const changes = {};
    for (const key in newer) {
      if (
        typeof newer[key] === 'object' &&
        newer[key] !== null &&
        older[key] !== null
      ) {
        // Recursively check nested objects for changes
        const nestedChanges = getChangedFields(newer[key], older[key]);
        if (Object.keys(nestedChanges).length > 0) {
          changes[key] = nestedChanges;
        }
      } else if (!isNaN(newer[key]) && !isNaN(older[key])) {
        // Explicitly handle numeric comparisons
        if (Number(newer[key]) !== Number(older[key])) {
          changes[key] = { old: older[key], new: newer[key] };
        }
      } else if (dayjs(newer[key]).isValid() && dayjs(older[key]).isValid()) {
        // Handle date comparisons
        const formattedNewer = dayjs(newer[key]).format('YYYY-MM-DD');
        const formattedOlder = dayjs(older[key]).format('YYYY-MM-DD');
        if (formattedNewer !== formattedOlder) {
          changes[key] = { old: older[key], new: newer[key] };
        }
      } else if (newer[key] !== older[key]) {
        // Handle other types of changes
        changes[key] = { old: older[key], new: newer[key] };
      }
    }
    return changes;
  };

  // Function to format field names for display
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z][a-z])/g, ' $1') // Insert space before capital letter followed by lowercase letter
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space between lowercase and capital letter
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize the first letter
      .trim(); // Remove any leading/trailing spaces
  };

  // Function to render the changes in the history data
  const renderChanges = () => {
    if (historyData.length === 0) return null;

    // Sort history data by version in descending order
    const sortedHistory = [...historyData].sort(
      (a, b) => b.version - a.version
    );
    const changesList = [];

    // Get the changes between each version
    for (let i = 0; i < sortedHistory.length - 1; i++) {
      const changes = getChangedFields(sortedHistory[i], sortedHistory[i + 1]);
      changesList.push({ version: sortedHistory[i].version, changes });
    }

    // Fields to ignore when displaying changes
    const ignoredFields = [
      'id',
      'initialETDFormatted',
      'initialETAFormatted',
      'actualETDFormatted',
      'actualETAFormatted',
      'changed',
      'departurePortName',
      'departureCountryName',
      'destinationPortName',
      'destinationCountryName',
      'status',
      'shippingCompanyName',
      'portOperatorName',
    ];

    // Render the changes in an accordion
    return changesList.map((change, index) => (
      <Accordion
        key={index}
        defaultExpanded={index === 0}
        sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.09) }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${index + 1}-content`}
          id={`panel${index + 1}-header`}
        >
          <Typography>Version {change.version}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ul style={{ listStyleType: 'none', padding: 0 }}>
            {Object.keys(change.changes)
              .filter((field) => !ignoredFields.includes(field))
              .map((field, idx) => {
                let oldValue = change.changes[field].old;
                let newValue = change.changes[field].new;

                const nestedFields = [
                  'destinationPort',
                  'departurePort',
                  'destinationCountry',
                  'departureCountry',
                  'shippingCompany',
                  'portOperator',
                ];

                if (nestedFields.includes(field)) {
                  if (field === 'shippingCompany' || field === 'portOperator') {
                    const subFields = ['supplierNo', 'supplierName'];
                    oldValue = subFields
                      .map((subField) => change.changes[field][subField].old)
                      .join(' ');
                    newValue = subFields
                      .map((subField) => change.changes[field][subField].new)
                      .join(' ');
                  } else {
                    oldValue = change.changes[field].name.old;
                    newValue = change.changes[field].name.new;
                  }
                }

                const isNumeric = !isNaN(oldValue) && !isNaN(newValue);
                const isDate =
                  dayjs(oldValue).isValid() && dayjs(newValue).isValid();
                let IconComponent = KeyboardDoubleArrowRightOutlinedIcon;
                let iconColor = 'inherit';

                // Determine the icon and color based on the type of change
                const getIconAndColor = (isIncrease, isDelayField) => {
                  const IconComponent = isIncrease
                    ? KeyboardDoubleArrowUpOutlinedIcon
                    : KeyboardDoubleArrowDownOutlinedIcon;
                  const iconColor = isIncrease
                    ? isDelayField
                      ? theme.palette.error.main
                      : theme.palette.success.main
                    : isDelayField
                      ? theme.palette.success.main
                      : theme.palette.error.main;
                  return { IconComponent, iconColor };
                };

                if (isNumeric) {
                  const isIncrease = newValue > oldValue;
                  const isDelayField =
                    field === 'departureDelay' ||
                    field === 'arrivalDelay' ||
                    field === 'duration';
                  ({ IconComponent, iconColor } = getIconAndColor(
                    isIncrease,
                    isDelayField
                  ));
                } else if (isDate) {
                  const oldDate = dayjs(oldValue).format('YYYY-MM-DD');
                  const newDate = dayjs(newValue).format('YYYY-MM-DD');
                  const isIncrease = newDate > oldDate;
                  ({ IconComponent, iconColor } = getIconAndColor(
                    isIncrease,
                    true
                  ));
                  oldValue = formatDate(oldDate);
                  newValue = formatDate(newDate);
                }

                return (
                  <li key={idx}>
                    <Typography marginTop={2}>
                      <strong>{formatFieldName(field)}:</strong>{' '}
                    </Typography>
                    <Grid
                      container
                      spacing={2}
                      alignItems='center'
                      justifyContent='center'
                    >
                      <Grid item xs={5}>
                        <Paper
                          elevation={12}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '40px',
                          }}
                        >
                          {oldValue}
                        </Paper>
                      </Grid>
                      <Grid
                        item
                        xs={2}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <IconComponent
                          sx={{
                            color: isNumeric || isDate ? iconColor : 'inherit',
                          }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Paper
                          elevation={12}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '40px',
                          }}
                        >
                          {newValue}
                        </Paper>
                      </Grid>
                    </Grid>
                  </li>
                );
              })}
          </ul>
        </AccordionDetails>
      </Accordion>
    ));
  };

  // Function to handle dialog close and reset state
  const handleDialogClose = () => {
    setHistoryData([]);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth={true}
      maxWidth='md'
      PaperProps={{
        elevation: 2,
        sx: { backgroundColor: 'background.paper' },
      }}
    >
      <DialogTitle>History</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {row
            ? `See the history of changes for ${row.vessel}`
            : 'No data available'}
        </DialogContentText>
        {row && renderChanges()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogComponentHistory;
